import { useState, useEffect, useMemo } from 'react';
import { Calendar, CalendarEvent } from '@/components/ui/calendar';
import { DateFormat, formatDate } from '@/data/format/format-date';
import { addDays, differenceInDays, subDays } from 'date-fns';
import { ArrowLeftCircle, ArrowRightCircle, CalendarIcon } from 'lucide-react';
import { courtApi } from '@repo/api-client';
import { Button } from '@repo/ui/components/button';

interface CourtTimelineProps {
  clubId: string;
  courtId: string;
  initialDate?: Date;
  onDateChange?: (date: Date) => void;
}

interface ApiReservation {
  id: string;
  start: string;
  end: string;
  type: 'TOURNAMENT' | 'NORMAL';
  phase: 'GROUP_PHASE' | 'ELIMINATION_PHASE' | null;
  assigned: boolean;
  tournament: { id: string; name: string } | null;
}

export function CourtTimeline({ clubId, courtId, initialDate, onDateChange }: CourtTimelineProps) {
  const [date, setDate] = useState<Date>(initialDate || new Date());
  const [reservations, setReservations] = useState<ApiReservation[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch reservations when date or courtId changes
  useEffect(() => {
    const fetchReservations = async () => {
      setLoading(true);
      try {
        const dateStr = formatDate(date, DateFormat.YYYY_MM_DD);
        const { results } = await courtApi.getReservations(clubId, courtId, dateStr);
        setReservations(results as ApiReservation[]);
      } catch (error) {
        console.error('Error fetching reservations:', error);
        setReservations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReservations();
  }, [clubId, courtId, date]);

  const handleNavigate = (newDate: Date) => {
    setDate(newDate);
    onDateChange?.(newDate);
  };

  const dateTitle = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    const dayDifference = differenceInDays(compareDate, today);

    if (dayDifference === 0) {
      return 'Hoy';
    }
    if (dayDifference === 1) {
      return 'Mañana';
    }
    if (dayDifference === -1) {
      return 'Ayer';
    }
    return date.toLocaleDateString();
  }, [date]);

  // Transform reservations to calendar events with extended data
  const events: CalendarEvent[] = useMemo(() => {
    return reservations.map((r) => {
      let title = 'Reserva';

      if (r.type === 'TOURNAMENT' && r.tournament) {
        const phaseLabel = r.phase === 'GROUP_PHASE' ? 'Fase de Grupos' : 'Fase de Eliminación';
        title = `[TORNEO] ${r.tournament.name} · ${phaseLabel}`;
      } else if (r.type === 'NORMAL') {
        title = '[NORMAL] Reserva';
      }

      return {
        id: r.id,
        title,
        start: new Date(r.start),
        end: new Date(r.end),
        type: r.type,
        phase: r.phase,
        assigned: r.assigned,
        tournament: r.tournament,
      };
    });
  }, [reservations]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between gap-2 p-4">
        <div className="font-medium">{dateTitle}</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleNavigate(subDays(date, 1))}>
            <ArrowLeftCircle className="size-5" />
          </Button>
          <Button variant="outline" onClick={() => handleNavigate(new Date())}>
            <CalendarIcon className="size-5" />
          </Button>
          <Button variant="outline" onClick={() => handleNavigate(addDays(date, 1))}>
            <ArrowRightCircle className="size-5" />
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          Cargando...
        </div>
      ) : (
        <Calendar events={events} date={date} onNavigate={handleNavigate} />
      )}
    </div>
  );
}
